// Small helpers used across Quote Builder components.
// Canonical urgency band for the 0–10 desperation slider. Single source of
// truth shared by the Quote Builder slider chip AND the View Quotes column so
// the name (Critical/High/Watching/Chill) is always consistent. Colour + motion
// scale with severity; `unset` nudges staff to actually set it.
export function urgencyBand(desp, paid = false) {
  if (paid) return { key: "paid", label: "Paid", tone: "ok", anim: "", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" };
  const d = Number(desp) || 0;
  if (d >= 8) return { key: "critical", label: "Critical", tone: "red",    anim: "urg-critical", cls: "bg-red-500/15 text-red-300 border-red-500/50" };
  if (d >= 5) return { key: "high",     label: "High",     tone: "warn",   anim: "urg-high",     cls: "bg-amber-500/15 text-amber-300 border-amber-500/50" };
  if (d >= 2) return { key: "watching", label: "Watching", tone: "orange", anim: "urg-watching", cls: "bg-orange-500/15 text-orange-300 border-orange-500/40" };
  return        { key: "chill", label: "Chill", tone: "", anim: "", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" };
}


export const GBP = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

// UK standard VAT. BigCommerce `BCProduct.price` is stored tax-INCLUSIVE,
// so the true ex-VAT sell price is price / (1 + VAT_RATE).
export const VAT_RATE = 0.20;
export const exVatFromInc = (incPrice) => {
  const v = Number(incPrice);
  if (!Number.isFinite(v) || v <= 0) return null;
  return v / (1 + VAT_RATE);
};

// unitPrice = cost / (1 - margin/100)  (margin clamped -95..95 — negative
// margins are allowed so a deliberate below-cost price can be represented).
export const unitFromCostMargin = (cost, marginPct) => {
  const c = Number(cost) || 0;
  const m = Math.max(-95, Math.min(95, Number(marginPct) || 0));
  if (m === 0) return c;
  return c / (1 - m / 100);
};

// Effective (true) cost after a supplier rebate. Rebate only ever exceeds 0
// when a Galaxy supplier is assigned, so this is a no-op otherwise — the
// margin/floor maths keep using list cost until a real rebate is set.
export const effectiveCost = (cost, rebatePct) => {
  const c = Number(cost) || 0;
  const r = Math.max(0, Math.min(100, Number(rebatePct) || 0));
  return r > 0 ? c * (1 - r / 100) : c;
};

// ── Tier pricing (Task Module 07) ────────────────────────────────────
// Tier is a DISCOUNT off the live retail price (ex VAT), not a margin.
// tier_price = round(base * (1 - discount%/100), 2). Margin is derived.
export const tierUnitFromBase = (base, discountPct) => {
  const b = Number(base) || 0;
  const d = Math.max(0, Math.min(100, Number(discountPct) || 0));
  return Math.round(b * (1 - d / 100) * 100) / 100;
};
// margin% derived from a unit price + cost (both ex VAT).
export const marginFromUnit = (unit, cost) => {
  const u = Number(unit) || 0;
  const c = Number(cost) || 0;
  return u > 0 ? (1 - c / u) * 100 : 0;
};
// discount% off retail implied by a unit price.
export const discountFromUnit = (base, unit) => {
  const b = Number(base) || 0;
  const u = Number(unit) || 0;
  return b > 0 ? (1 - u / b) * 100 : 0;
};

// Profitability floor (guardrail warning, not a price setter). Fallback only —
// live thresholds come from /config/pricing-rules.
export const MARGIN_FLOOR = 6;

// Lowest unit price that still clears a given margin floor: cost / (1 - floor/100).
export const floorUnitFromCost = (cost, floorPct) => {
  const c = Number(cost) || 0;
  const f = Math.max(0, Math.min(99, Number(floorPct) || 0));
  return f === 0 ? c : c / (1 - f / 100);
};

// ── Margin band system (v3) — drives chip + hero + footer ──────────────
export const MARGIN_BANDS = [
  { key: "very_lean", label: "Very Lean",      min: 0,  max: 5,        colour: "#ef4444" }, // red
  { key: "lean",      label: "Lean Margin",    min: 5,  max: 10,       colour: "#f59e0b" }, // amber
  { key: "healthy",   label: "Healthy Margin", min: 10, max: 15,       colour: "#34d399" }, // green
  { key: "great",     label: "Great Margin",   min: 15, max: Infinity, colour: "#2dd4bf" }, // teal
];
export const bandOf = (pct) => {
  const p = Number(pct) || 0;
  if (p < 5) return MARGIN_BANDS[0];
  if (p < 10) return MARGIN_BANDS[1];
  if (p < 15) return MARGIN_BANDS[2];
  return MARGIN_BANDS[3];
};

// L / M / H margin presets (per Niraj's brief 2026-02)
export const MARGIN_PRESETS = { L: 3, M: 6, H: 9 };

// Placeholder delivery bands (§Q9). Editable via Settings later.
export const DELIVERY_BANDS = [
  { key: "band-lt-250",   label: "Under £250",  min: 0,   max: 249.99, cost: 70 },
  { key: "band-250-500",  label: "£250 – £500", min: 250, max: 499.99, cost: 50 },
  { key: "band-gt-500",   label: "Over £500",   min: 500, max: Infinity, cost: 0  },
];

export const suggestDeliveryBand = (subtotal) => {
  const s = Number(subtotal) || 0;
  return DELIVERY_BANDS.find((b) => s >= b.min && s <= b.max) || DELIVERY_BANDS[0];
};

// Spec's 6 verbatim Pipedrive stages
export const QUOTE_STAGES = [
  "Do Quote · Working · Follow-up",
  "Quote Sent · Negotiation",
  "Hold · No Answer",
  "Waiting Payment",
  "Won · Ordered",
  "Lost · Frozen · Archive",
];

export const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

// Real/blended margin + grand total at save time. Mirrors SummaryPanel.jsx
// exactly (RWA45 branch deal + supplier rebate) so the audit stamp matches the
// figure staff see on screen.
export function quoteMarginSummary(quote, subtotal, delivery = {}) {
  const RWA45_SKUS = ["50300", "50302", "50303"];
  const RWA45_NAMES = ["galaxy - west london", "galaxy - east london"];
  const supplier = String(quote?.Supplier?.name || "").trim().toLowerCase();
  const active = RWA45_NAMES.includes(supplier);
  const lines = quote?.LineItems || [];
  const qualifies = (it) => active && RWA45_SKUS.some((n) => String(it.sku || "").includes(n));
  const totalCost = lines.reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0);
  const branchDisc = lines.reduce((s, it) => s + (qualifies(it) ? (Number(it.cost) || 0) * (Number(it.qty) || 0) * 0.05 : 0), 0);
  const adjusted = totalCost - branchDisc;
  const rebatePct = Number(quote?.RebatePct) || 0;
  const netCost = adjusted - adjusted * (rebatePct / 100);
  const realMarginPct = subtotal > 0 ? ((subtotal - netCost) / subtotal) * 100 : 0;
  const dcost = Number(delivery?.cost) || 0;
  const grand = subtotal + dcost + (subtotal + dcost) * 0.2;
  return { realMarginPct, grand, rebatePct };
}

// ─── Discount vs website (staff-only) ───────────────────────────────────
// basePrice = the product's live website sell price ex VAT (set on add).
// Custom lines without a website price are excluded.
export const lineWebDiscPct = (it) => {
  const b = Number(it?.basePrice) || 0;
  return b > 0 ? discountFromUnit(b, it.unitPrice) : null;
};
export function websiteDiscount(lines = []) {
  let web = 0, sell = 0, count = 0;
  for (const it of lines) {
    const b = Number(it.basePrice) || 0;
    if (b <= 0) continue;
    const q = Number(it.qty) || 0;
    web += b * q; sell += (Number(it.unitPrice) || 0) * q; count += 1;
  }
  const disc = web - sell;
  return { web, sell, disc, pct: web > 0 ? (disc / web) * 100 : 0, count };
}

// ─── Quote completion % — keep in sync with backend services/quote_completeness.py
export const COMPLETION_FIELDS = [
  ["Customer", (c) => c.firstName || c.lastName || c.company],
  ["Project name / postcode", (c, d, q) => String(q.ProjectRef || "").trim()],
  ["Mobile", (c) => c.phone],
  ["Delivery address / postcode", (c, d, q, b) => ((d.addressSource && d.addressSource !== "invoice")
    ? d.deliveryAddress?.postcode
    : (b?.postcode || c.billingAddress?.postcode || c.postcode))],
  ["Build type", (c, d, q) => q.BuildType],
  ["Target date (or ASAP)", (c, d, q) => q.Timeline?.targetDate || q.Timeline?.asap],
  ["Urgency", (c, d, q) => Number(q.Timeline?.desperation) > 0],
  ["Delivery method", (c, d) => d.method || d.band],
  ["Delivery window", (c, d) => d.deliveryWindow],
  ["Site contact", (c, d) => d.siteContactName],
  ["Access", (c, d) => d.access],
  ["Parking", (c, d) => d.parking],
  ["Weight estimate", (c, d) => d.estimatedWeightKg],
  ["Customer site notes", (c, d) => String(d.notes || "").trim()],
  ["On-site facts asked", (c, d) => d.onSiteChecked || onsiteAllAnswered(d)],
];
export function quoteCompletion(quote = {}, delivery, billing) {
  const c = quote.Customer || {}, d = delivery || quote.Delivery || {};
  const ignored = new Set(quote.CompletionIgnored || []);
  // Ignored = hidden from the to-do list but still counted as empty in the %.
  const empty = COMPLETION_FIELDS.filter(([k, f]) => !f(c, d, quote, billing)).map(([k]) => k);
  const total = COMPLETION_FIELDS.length;
  const filled = total - empty.length;
  return { filled, total, pct: Math.round((filled / total) * 100), missing: empty.filter((k) => !ignored.has(k)), ignored: empty.filter((k) => ignored.has(k)) };
}

// Auto-urgency (1–10) from days-to-target + quote value. Over £3.5k or ≤3 days = Urgent.
export const URGENT_VALUE = 3500;
export function autoUrgency(quote = {}, total = 0) {
  const t = quote.Timeline || {};
  if (t.asap) return 10;
  const days = t.targetDate ? Math.ceil((new Date(t.targetDate) - new Date()) / 86400000) : null;
  let u = days == null ? 1 : days <= 3 ? 9 : days <= 7 ? 7 : days <= 14 ? 5 : days <= 30 ? 3 : 1;
  if (Number(total) > URGENT_VALUE) u = Math.max(u, 9);
  return u;
}
export const completionTone = (pct) => (pct === 100 ? "ok" : pct >= 67 ? "teal" : pct >= 34 ? "brass" : "red");

// ─── On-site facts: Yes / No / Unsure (booleans kept in sync for older readers)
export const ONSITE_FACTS = [
  { key: "people",   label: "People on site",                bool: "peopleOnSite" },
  { key: "forklift", label: "Forklift(s) available on site", bool: "forkliftAvailable" },
  { key: "kerbside", label: "Kerbside drop only",            bool: "kerbsideOnly" },
  { key: "parking",  label: "Parking restrictions",          bool: "parkingRestricted" },
];
export function onsiteAnswer(d = {}, key) {
  const a = d.onSiteAnswers?.[key];
  if (a) return a;
  const f = ONSITE_FACTS.find((x) => x.key === key);
  const legacy = key === "people" ? Number(d.peopleOnSite) > 0 : !!d[f.bool];
  return legacy ? "yes" : null;
}
export function onsitePatch(d = {}, key, answer) {
  const f = ONSITE_FACTS.find((x) => x.key === key);
  const answers = { ...(d.onSiteAnswers || {}), [key]: answer };
  if (key === "people") {
    const cur = Number(d.peopleOnSite) || 0;
    return answer === "yes"
      ? { onSiteAnswers: answers, peopleOnSite: Math.max(cur, Number(d.peopleOnSiteLast) || 0, 1) }
      : { onSiteAnswers: answers, peopleOnSite: 0, ...(cur > 0 && { peopleOnSiteLast: cur }) };
  }
  return { onSiteAnswers: answers, [f.bool]: answer === "yes" };
}
export const onsiteAllAnswered = (d = {}) => ONSITE_FACTS.every((f) => onsiteAnswer(d, f.key));
export const onsiteLabel = (a) => (a === "yes" ? "YES" : a === "no" ? "NO" : a === "unsure" ? "UNSURE" : "—");

// "CWQ-005477 - Hira Construction D&B Ltd - Sital Lachhani - CW Limited" — mirrors backend quote_file_title().
export const quoteFileTitle = (quote) => {
  const c = quote?.Customer || {};
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.name || "";
  const seen = new Set();
  const ref = quote?.QuoteRef || quote?._id;
  const head = quote?.QuoteVariant === "proforma" ? `${/^CWQ/i.test(ref || "") ? `CWP${String(ref).slice(3)}` : `CWP-${ref}`} Pro Forma Invoice` : ref;
  return [head, c.company, name, "CW Limited"]
    .map((p) => String(p || "").replace(/[\\/:*?"<>|\r\n]+/g, " ").trim())
    .filter((p) => p && !seen.has(p.toLowerCase()) && seen.add(p.toLowerCase()))
    .join(" - ");
};
